import assertNever from "assert-never";
import * as messages from "../../messages";
import { AgencyId, AgencyMember } from "../../messages";
import { CaregiverStatus } from "../../messages/CaregiverStatus";
import { STRINGS } from "../../strings";
import { Agency } from "../../system/AppState/AppAgenciesState";
import { HashMap } from "../../utils/HashMap";
import { List } from "../../utils/List";
import { comparable } from "../../utils/OrdTuple";

export type AgencyMembershipStatus = "Active" | "Requested" | "Terminated";

export interface AgenciesBlock {
  readonly status: CaregiverStatus;
  readonly agencies: List<AgencyBlock>;
}

export interface AgencyBlock {
  readonly agencyDetails: messages.Agency;
  readonly contactableAgencyMembers: List<ContactableAgencyMembersBlock>;
}

export interface ContactableAgencyMembersBlock {
  readonly department: string;
  readonly agencyMembers: List<messages.AgencyMember>;
}

export function statusTranslationKey(status: CaregiverStatus): STRINGS {
  switch (status) {
    case CaregiverStatus.PENDING:
      return STRINGS.PENDING;
    case CaregiverStatus.ACTIVE:
      return STRINGS.ACTIVE;
    case CaregiverStatus.ON_HOLD:
      return STRINGS.ON_HOLD;
    case CaregiverStatus.SUSPENDED:
      return STRINGS.INACTIVE;
    case CaregiverStatus.TERMINATED:
      return STRINGS.TERMINATED;
    case CaregiverStatus.JOIN_REQUEST:
      return STRINGS.REQUESTED;
    case CaregiverStatus.ON_LEAVE:
      return STRINGS.ON_LEAVE;
    case CaregiverStatus.QUIT:
      return STRINGS.ON_LEAVE;
    case CaregiverStatus.REJECTED:
      return STRINGS.REJECTED;
    default:
      return assertNever(status);
  }
}

export function convertToAgenciesBlocks(agencies: HashMap<AgencyId, Agency>): List<AgenciesBlock> {
  let agenciesMap = HashMap.empty<CaregiverStatus, List<Agency>>();

  for (const agency of agencies.values()) {
    agenciesMap = agenciesMap.update(
      (prev: List<Agency> | undefined): List<Agency> =>
        prev !== undefined ? prev.append(agency) : List.fromArray([agency]),
      agency.status
    );
  }

  const agencyBlocks = List.fromIterable<[CaregiverStatus, List<Agency>]>(
    agenciesMap.entries()
  ).map(([status, agenciesList]): AgenciesBlock => {
    return {
      status: status,
      agencies: agenciesList.sortBy((a) => a.agency.name).map(toAgencyBlock),
    };
  });

  return agencyBlocks.sortBy((a) => caregiverStatusSortOrder(a.status));
}

export function caregiverStatusSortOrder(status: CaregiverStatus): number {
  switch (status) {
    case CaregiverStatus.ACTIVE:
      return 1;
    case CaregiverStatus.JOIN_REQUEST:
      return 2;
    case CaregiverStatus.PENDING:
      return 3;
    case CaregiverStatus.ON_HOLD:
      return 4;
    case CaregiverStatus.ON_LEAVE:
      return 5;
    case CaregiverStatus.SUSPENDED:
      return 6;
    case CaregiverStatus.TERMINATED:
      return 7;
    case CaregiverStatus.QUIT:
      return 8;
    case CaregiverStatus.REJECTED:
      return 9;
    default:
      return assertNever(status);
  }
}

export function toAgencyBlock(agency: Agency): AgencyBlock {
  let departmentsMap = HashMap.empty<string, List<AgencyMember>>();
  for (const agencyMember of agency.agencyMembers.values()) {
    if (!(agencyMember.agencyAdmin || agencyMember.jobTitle === "Admin")) {
      departmentsMap = departmentsMap.update(
        (prev: List<AgencyMember> | undefined): List<AgencyMember> =>
          prev !== undefined ? prev.append(agencyMember) : List.fromArray([agencyMember]),
        agencyMemberDepartment(agencyMember.jobTitle)
      );
    }
  }

  const agencyMembersBlock = List.fromIterable<[string, List<messages.AgencyMember>]>(
    departmentsMap.entries()
  ).map(([department, agencyMembers]): ContactableAgencyMembersBlock => {
    return {
      department: department,
      agencyMembers: agencyMembers.sortBy((x) => comparable(x.firstName, x.lastName)),
    };
  });

  return {
    agencyDetails: agency.agency,
    contactableAgencyMembers: agencyMembersBlock,
  };
}

export function caregiverStatusToAgencyMembershipStatus(
  caregiverStatus: CaregiverStatus
): AgencyMembershipStatus | null {
  switch (caregiverStatus) {
    case CaregiverStatus.ACTIVE:
      return "Active";
    case CaregiverStatus.JOIN_REQUEST:
      return "Requested";
    case CaregiverStatus.TERMINATED:
      return "Terminated";
    case CaregiverStatus.PENDING:
    case CaregiverStatus.ON_HOLD:
    case CaregiverStatus.ON_LEAVE:
    case CaregiverStatus.SUSPENDED:
    case CaregiverStatus.QUIT:
    case CaregiverStatus.REJECTED:
      return null;
    default:
      return assertNever(caregiverStatus);
  }
}

export function agencyMemberDepartment(jobTitle: string) {
  switch (jobTitle) {
    case "Coordinator":
    case "SeniorCoordinator":
      return "Coordinator";
    default:
      return jobTitle;
  }
}
